import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, ExternalLink, FileText, Folder, Loader2, Paperclip, Star } from 'lucide-react';
import { useDocsOrg, useDocsOrgSop, type DocsOrgDetail, type SopAsset } from '@/api/docs';
import { SopList, useSopViewer } from '@/components/docs/Sop';
import { SiteSummaryPanel } from '@/components/docs/SiteSummary';
import { PageHeader } from '@/components/PageHeader';
import { DesktopOnly } from '@/components/dashboard/DesktopOnly';
import { SearchInput } from '@/components/ui/search-input';
import { DocumentViewer } from '@/components/docs/DocumentViewer';
import { AGENT_TYPES, ExpiryChip, RmmCell, kindLabel } from '@/components/docs/DocsKit';
import { Chip, Empty, Th, theadCls } from '@/components/network/NetworkKit';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { ROUTES, companyPath } from '@/lib/routes';
import { cn } from '@/lib/utils';

/**
 * One IT Glue organisation: alert, Site Summary and quick notes up top, then tabs for documents (opened in the
 * in-app viewer), configurations (with their Datto RMM device), contacts, domains/SSL and
 * expirations. Read-only; passwords are never fetched — "Open in IT Glue" for anything else.
 */

type Tab = 'documents' | 'sop' | 'configurations' | 'contacts' | 'domains' | 'expirations';
const TABS: { id: Tab; label: string; count: (d: DocsOrgDetail, sop: SopAsset[]) => number }[] = [
  { id: 'documents',      label: 'Documents',      count: (d) => d.documents.length },
  { id: 'sop',            label: 'On/Offboarding', count: (_d, sop) => sop.length },
  { id: 'configurations', label: 'Configurations', count: (d) => d.configurations.filter((c) => !c.archived).length },
  { id: 'contacts',       label: 'Contacts',       count: (d) => d.contacts.length },
  { id: 'domains',        label: 'Domains & SSL',  count: (d) => d.domains.length + d.ssl.length },
  { id: 'expirations',    label: 'Expirations',    count: (d) => d.expirations.length },
];

export default function DocsOrganizationPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const { data, isLoading, error, isFetching } = useDocsOrg(orgId);
  const sop = useDocsOrgSop(orgId);
  const sopItems = sop.data?.items ?? [];
  const [params, setParams] = useSearchParams();
  const tab = (TABS.find((t) => t.id === params.get('tab'))?.id ?? 'documents') as Tab;
  const setTab = (t: Tab) => setParams(t === 'documents' ? {} : { tab: t }, { replace: true });
  const [docId, setDocId] = useState<string | null>(null);
  const o = data?.org;

  return (
    <DesktopOnly title="Docs is available on desktop" message="Open Cosmos on a larger screen to view documentation.">
      <div className="mx-auto w-full max-w-[1500px] px-4 py-4 md:px-6">
        <Link to={ROUTES.docs.organizations} className="mb-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3 w-3" /> Organisations
        </Link>
        {isLoading ? (
          <div className="flex items-center gap-2 py-16 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading from IT Glue…</div>
        ) : error ? (
          <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error instanceof Error ? error.message : 'Failed to load'}</div>
        ) : data && o ? (
          <>
            <PageHeader title={o.name} isFetching={isFetching}
              subtitle={<>
                {o.type ?? 'Organisation'}{o.status && o.status !== 'Active' ? ` · ${o.status}` : ''} ·{' '}
                {o.autotaskCompanyId != null
                  ? <Link to={companyPath(o.autotaskCompanyId)} className="hover:underline">Orbit: {o.companyName ?? `#${o.autotaskCompanyId}`}</Link>
                  : <span className="italic">not linked to an Autotask company in IT Glue</span>}
              </>}
              actions={o.url && (
                <a href={o.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs text-foreground hover:border-ring">
                  Open in IT Glue <ExternalLink className="h-3 w-3" />
                </a>
              )} />

            {o.alert && (
              <div className="mt-3 flex items-start gap-2 rounded-md border border-warning/30 bg-warning-soft px-3 py-2 text-sm text-warning">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /><span className="whitespace-pre-line">{o.alert}</span>
              </div>
            )}
            {/* IT Glue "Site Summary" flexible asset(s): status, known issues, key contacts, team. */}
            <SiteSummaryPanel orgId={o.id} />
            {(o.quickNotes || o.description) && (
              <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
                {o.quickNotes && <NoteBox title="Quick notes" text={o.quickNotes} />}
                {o.description && <NoteBox title="Description" text={o.description} />}
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-1 rounded-lg bg-muted p-1" style={{ width: 'fit-content' }}>
              {TABS.map((t) => (
                <button key={t.id} type="button" onClick={() => setTab(t.id)}
                  className={cn('rounded-md px-3 py-1 text-xs font-medium', tab === t.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>
                  {t.label} <span className="ml-1 tabular-nums text-muted-foreground">{t.count(data, sopItems)}</span>
                </button>
              ))}
            </div>

            {tab === 'documents' && <DocumentsTab data={data} open={setDocId} />}
            {tab === 'sop' && <SopTab items={sopItems} loading={sop.isLoading} />}
            {tab === 'configurations' && <ConfigurationsTab data={data} />}
            {tab === 'contacts' && <ContactsTab data={data} />}
            {tab === 'domains' && <DomainsTab data={data} />}
            {tab === 'expirations' && <ExpirationsTab data={data} />}
            <DocumentViewer id={docId} onClose={() => setDocId(null)} />
          </>
        ) : null}
      </div>
    </DesktopOnly>
  );
}

function NoteBox({ title, text }: { title: string; text: string }) {
  return (
    <section className="rounded-lg border border-border bg-card px-4 py-3 shadow-sm">
      <h2 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</h2>
      <p className="max-h-48 overflow-y-auto whitespace-pre-line text-sm text-foreground">{text}</p>
    </section>
  );
}

const wrap = 'mt-3 overflow-x-auto rounded-lg border border-border bg-card shadow-sm';

/** IT Glue "SOP On/Offboarding" flexible asset records for this org (passwords never shown). */
function SopTab({ items, loading }: { items: SopAsset[]; loading: boolean }) {
  const { setOpen, viewer } = useSopViewer();
  if (loading) return <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading SOPs…</div>;
  if (!items.length) return <p className="mt-4 text-sm text-muted-foreground">No SOP On/Offboarding records for this organisation in IT Glue.</p>;
  return (
    <div className="mt-3 max-w-xl rounded-lg border border-border bg-card p-3 shadow-sm">
      <SopList items={items} onOpen={setOpen} />
      {viewer}
    </div>
  );
}

function DocumentsTab({ data, open }: { data: DocsOrgDetail; open: (id: string) => void }) {
  const [q, setQ] = useState('');
  const groups = useMemo(() => {
    const s = q.trim().toLowerCase();
    const m = new Map<string, DocsOrgDetail['documents']>();
    for (const d of data.documents) {
      if (s && !d.name.toLowerCase().includes(s) && !(d.folder ?? '').toLowerCase().includes(s)) continue;
      const k = d.folder ?? '';
      m.set(k, [...(m.get(k) ?? []), d]);
    }
    return [...m.entries()].sort(([a], [b]) => (a === '' ? -1 : b === '' ? 1 : a.localeCompare(b)));
  }, [data.documents, q]);

  return (
    <div className="mt-3">
      <SearchInput withIcon placeholder="Document or folder" className="w-72" value={q} onValueChange={setQ} />
      <div className={wrap}>
        {groups.length === 0 && <div className="px-4 py-10 text-center text-sm text-muted-foreground">{data.documents.length ? 'Nothing matches.' : 'No documents for this organisation.'}</div>}
        {groups.map(([folder, docs]) => (
          <div key={folder} className="border-b border-divider last:border-0">
            <div className="flex items-center gap-1.5 bg-muted/40 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              <Folder className="h-3 w-3" /> {folder || 'Top level'} <span className="font-normal tabular-nums">· {docs.length}</span>
            </div>
            <ul className="divide-y divide-divider">
              {docs.map((d) => (
                <li key={d.id}>
                  <button type="button" onClick={() => open(d.id)} className="flex w-full items-center justify-between gap-3 px-4 py-2 text-left text-sm hover:bg-muted/30">
                    <span className="flex min-w-0 items-center gap-2">
                      {d.uploaded ? <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /> : <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                      <span className="truncate text-foreground">{d.name}</span>
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">{d.updatedAt ? fmtRelative(d.updatedAt) : ''}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function ConfigurationsTab({ data }: { data: DocsOrgDetail }) {
  const [archived, setArchived] = useState(false);
  const [q, setQ] = useState('');
  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return data.configurations
      .filter((c) => archived || !c.archived)
      .filter((c) => !s || `${c.name} ${c.serial ?? ''} ${c.type ?? ''} ${c.model ?? ''} ${c.contact ?? ''} ${c.ip ?? ''}`.toLowerCase().includes(s))
      .sort((a, b) => (a.type ?? '').localeCompare(b.type ?? '') || a.name.localeCompare(b.name));
  }, [data.configurations, archived, q]);
  const noAgent = data.configurations.filter((c) => !c.archived && AGENT_TYPES.test(c.type ?? '') && !c.rmm).length;

  return (
    <div className="mt-3">
      <div className="flex items-center gap-3">
        <SearchInput withIcon placeholder="Name, serial, model, contact, IP" className="w-72" value={q} onValueChange={setQ} />
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground"><input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} /> Show archived</label>
        {noAgent > 0 && <span className="text-xs text-warning">{noAgent} computer{noAgent === 1 ? '' : 's'} without a live Datto RMM device</span>}
      </div>
      <div className={wrap}>
        <table className="w-full text-sm" style={{ minWidth: 1100 }}>
          <thead className={theadCls}><tr><Th>Name</Th><Th>Type</Th><Th>Make / model</Th><Th>Serial</Th><Th>OS</Th><Th>Contact</Th><Th>Datto RMM</Th><Th>Warranty</Th><Th /></tr></thead>
          <tbody className="divide-y divide-divider">
            {rows.length === 0 && <Empty cols={9} />}
            {rows.map((c) => (
              <tr key={c.id} className={cn('hover:bg-muted/30', c.archived && 'opacity-60')}>
                <td className="px-3 py-2"><div className="max-w-[220px] truncate font-medium text-foreground" title={c.name}>{c.name}</div>{c.ip && <div className="text-[11px] text-muted-foreground">{c.ip}</div>}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{c.type ?? '—'}{c.archived && <span className="ml-1 italic">· archived</span>}</td>
                <td className="px-3 py-2 text-xs"><div className="max-w-[170px] truncate" title={[c.manufacturer, c.model].filter(Boolean).join(' ')}>{[c.manufacturer, c.model].filter(Boolean).join(' ') || '—'}</div></td>
                <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground"><div className="max-w-[130px] truncate" title={c.serial ?? ''}>{c.serial ?? '—'}</div></td>
                <td className="px-3 py-2 text-xs text-muted-foreground"><div className="max-w-[150px] truncate" title={c.os ?? ''}>{c.os ?? '—'}</div></td>
                <td className="px-3 py-2 text-xs"><div className="max-w-[140px] truncate" title={c.contact ?? ''}>{c.contact ?? '—'}</div></td>
                <td className="px-3 py-2"><RmmCell c={c} /></td>
                <td className="px-3 py-2"><ExpiryChip date={c.warrantyExpires} /></td>
                <td className="px-3 py-2 text-right">{c.url && <a href={c.url} target="_blank" rel="noreferrer" title="Open in IT Glue" className="text-muted-foreground hover:text-foreground"><ExternalLink className="h-3.5 w-3.5" /></a>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ContactsTab({ data }: { data: DocsOrgDetail }) {
  const [q, setQ] = useState('');
  const s = q.trim().toLowerCase();
  const rows = data.contacts.filter((c) => !s || `${c.name} ${c.title ?? ''} ${c.emails.join(' ')}`.toLowerCase().includes(s));
  return (
    <div className="mt-3">
      <SearchInput withIcon placeholder="Name, title or email" className="w-72" value={q} onValueChange={setQ} />
      <div className={wrap}>
        <table className="w-full text-sm">
          <thead className={theadCls}><tr><Th>Name</Th><Th>Title</Th><Th>Type</Th><Th>Email</Th><Th>Autotask</Th><Th /></tr></thead>
          <tbody className="divide-y divide-divider">
            {rows.length === 0 && <Empty cols={6} />}
            {rows.map((c) => (
              <tr key={c.id} className="hover:bg-muted/30">
                <td className="px-3 py-2 font-medium text-foreground">{c.important && <Star className="mr-1 inline h-3 w-3 fill-warning text-warning" aria-label="Important" />}{c.name}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{c.title ?? '—'}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{c.type ?? '—'}</td>
                <td className="px-3 py-2 text-xs">{c.emails.join(', ') || '—'}</td>
                <td className="px-3 py-2 text-xs">{c.autotaskContactId != null ? <span className="text-muted-foreground">#{c.autotaskContactId}</span> : <span className="italic text-muted-foreground">not linked</span>}</td>
                <td className="px-3 py-2 text-right">{c.url && <a href={c.url} target="_blank" rel="noreferrer" title="Open in IT Glue" className="text-muted-foreground hover:text-foreground"><ExternalLink className="h-3.5 w-3.5" /></a>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DomainsTab({ data }: { data: DocsOrgDetail }) {
  return (
    <div className="mt-3 grid grid-cols-1 gap-4 xl:grid-cols-2">
      <div className="overflow-x-auto rounded-lg border border-border bg-card shadow-sm">
        <table className="w-full text-sm">
          <thead className={theadCls}><tr><Th>Domain</Th><Th>Registrar</Th><Th>Expires</Th></tr></thead>
          <tbody className="divide-y divide-divider">
            {data.domains.length === 0 && <Empty cols={3} text="No domains recorded." />}
            {data.domains.map((d) => (
              <tr key={d.id}>
                <td className="px-3 py-2">{d.url ? <a href={d.url} target="_blank" rel="noreferrer" className="font-medium text-foreground hover:underline">{d.name}</a> : d.name}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{d.registrar ?? '—'}</td>
                <td className="px-3 py-2"><ExpiryChip date={d.expiresOn} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="overflow-x-auto rounded-lg border border-border bg-card shadow-sm">
        <table className="w-full text-sm">
          <thead className={theadCls}><tr><Th>SSL certificate</Th><Th>Issuer</Th><Th>Valid until</Th></tr></thead>
          <tbody className="divide-y divide-divider">
            {data.ssl.length === 0 && <Empty cols={3} text="No SSL certificates recorded." />}
            {data.ssl.map((c) => (
              <tr key={c.id}>
                <td className="px-3 py-2">{c.url ? <a href={c.url} target="_blank" rel="noreferrer" className="font-medium text-foreground hover:underline">{c.host || '—'}</a> : c.host}</td>
                <td className="max-w-[220px] truncate px-3 py-2 text-xs text-muted-foreground">{c.issuer ?? '—'}</td>
                <td className="px-3 py-2"><ExpiryChip date={c.validUntil} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ExpirationsTab({ data }: { data: DocsOrgDetail }) {
  const [past, setPast] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const rows = data.expirations.filter((e) => past || (e.date ?? '') >= today);
  return (
    <div className="mt-3">
      <label className="flex items-center gap-1.5 text-xs text-muted-foreground"><input type="checkbox" checked={past} onChange={(e) => setPast(e.target.checked)} /> Include already expired ({data.expirations.length - data.expirations.filter((e) => (e.date ?? '') >= today).length})</label>
      <div className={wrap}>
        <table className="w-full text-sm">
          <thead className={theadCls}><tr><Th>Expires</Th><Th>Kind</Th><Th>Item</Th><Th>Description</Th><Th /></tr></thead>
          <tbody className="divide-y divide-divider">
            {rows.length === 0 && <Empty cols={5} text="Nothing upcoming." />}
            {rows.map((e) => (
              <tr key={e.id}>
                <td className="px-3 py-2"><ExpiryChip date={e.date} /></td>
                <td className="px-3 py-2"><Chip tone="muted">{kindLabel(e.kind)}</Chip></td>
                <td className="px-3 py-2 font-medium text-foreground">{e.name || '—'}</td>
                <td className="max-w-[360px] truncate px-3 py-2 text-xs text-muted-foreground" title={e.description ?? ''}>{e.description ?? '—'}</td>
                <td className="px-3 py-2 text-right">{e.url && <a href={e.url} target="_blank" rel="noreferrer" title="Open in IT Glue" className="text-muted-foreground hover:text-foreground"><ExternalLink className="h-3.5 w-3.5" /></a>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
